'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown01Icon,
  Add01Icon,
  TerminalIcon,
  Delete02Icon,
} from '@terra/ui-icons';
import {
  getShellOptionsForPlatform,
  type ShellType,
  useTerminalTabs,
} from '../../../store/terminalStore';
import { Box, HStack } from '@terra/ui-styled-system';
import { LabelDefault, Menu } from '@terra/ui-design-system';
import { closeTerminalTabProcess } from './close-terminal-tab-process';
import type { TabActions } from './types';

export function useTerminalTabActions(): TabActions {
  const {
    terminalTabs,
    activeTerminalTab,
    addTerminalTab,
    removeTerminalTab,
    defaultShell,
  } = useTerminalTabs();
  const [availableShells, setAvailableShells] = useState<ShellType[]>(() =>
    getShellOptionsForPlatform({
      platform:
        typeof navigator !== 'undefined' &&
        navigator.userAgent.toLowerCase().includes('windows')
          ? 'windows'
          : 'darwin',
    }),
  );

  useEffect(() => {
    if (!window.electronAPI?.app?.getPlatform) {
      return;
    }

    void Promise.resolve(window.electronAPI.app.getPlatform()).then(
      (platform) => {
        setAvailableShells(getShellOptionsForPlatform({ platform }));
      },
    );
  }, []);

  return useMemo(() => {
    const activeShell: ShellType = activeTerminalTab?.shell || defaultShell;
    const createTerminalForShell = (shell: ShellType) => {
      const sessionId = `terminal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      addTerminalTab({
        id: sessionId,
        title: shell,
        sessionId,
        shell,
      });
    };

    const handleCreateNewTerminal = () => {
      createTerminalForShell(activeShell);
    };

    const handleKillTerminal = () => {
      if (activeTerminalTab) {
        closeTerminalTabProcess(activeTerminalTab).catch((error: unknown) => {
          console.warn(
            '[TerminalTabActions] Failed to kill session terminal',
            error,
          );
        });
        removeTerminalTab(activeTerminalTab.id);
      }
    };

    return {
      statusIndicator: activeShell
        ? {
            icon: <TerminalIcon size={12} />,
            label: activeShell,
          }
        : undefined,
      customControls: [
        <HStack
          key="terminal-add-split-button"
          gap="0"
          alignItems="center"
          borderRadius="sm"
          overflow="hidden"
          bg="bg.canvas"
        >
          <Box
            as="button"
            aria-label={`Add terminal (${activeShell})`}
            onClick={handleCreateNewTerminal}
            h="6"
            px="2"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            color="fg.muted"
            bg="transparent"
            _hover={{ bg: 'bg.subtle', color: 'fg.default' }}
            _active={{ bg: 'bg.muted' }}
          >
            <Add01Icon size={14} />
          </Box>
          <Menu.Root positioning={{ strategy: 'fixed', placement: 'top-end' }}>
            <Menu.Trigger asChild>
              <Box
                as="button"
                aria-label="Choose terminal shell to add"
                h="6"
                px="1.5"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                color="fg.muted"
                bg="transparent"
                _hover={{ bg: 'bg.subtle', color: 'fg.default' }}
                _active={{ bg: 'bg.muted' }}
              >
                <ArrowDown01Icon size={12} />
              </Box>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content maxH="120px" overflowY="auto" minW="110px" py="1">
                {availableShells.map((shellOption) => (
                  <Menu.Item
                    key={shellOption}
                    value={shellOption}
                    onClick={() => createTerminalForShell(shellOption)}
                    py="1"
                  >
                    <LabelDefault as="span">{shellOption}</LabelDefault>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        </HStack>,
      ],
      actions: [
        {
          id: 'kill-terminal',
          icon: <Delete02Icon size={14} />,
          label: 'Kill Terminal',
          onClick: handleKillTerminal,
          disabled: terminalTabs.length === 0,
        },
      ],
    };
  }, [
    terminalTabs.length,
    activeTerminalTab,
    defaultShell,
    availableShells,
    addTerminalTab,
    removeTerminalTab,
  ]);
}
